'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import TenantChatPanel from '@/components/TenantChatPanel'
import { useChatNotifications, requestNotificationPermission, type ChatOpenTarget, type ChatNotificationState } from '@/hooks/useChatNotifications'

type ChatPanelContextValue = {
  openChat: (target?: ChatOpenTarget) => void
  closeChat: () => void
  isOpen: boolean
  unreadCount: number
  resetUnread: () => void
  requestNotificationPermission: () => void
  notificationPermission: NotificationPermission | 'unsupported'
}

const ChatPanelContext = createContext<ChatPanelContextValue | null>(null)

export type { ChatOpenTarget }

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [openTarget, setOpenTarget] = useState<ChatOpenTarget | null>(null)

  const openChat = useCallback((target?: ChatOpenTarget) => {
    setOpenTarget(target ?? null)
    setOpen(true)
  }, [])

  const closeChat = useCallback(() => {
    setOpen(false)
    setOpenTarget(null)
  }, [])

  const openChatRef = useRef(openChat)
  openChatRef.current = openChat

  const notifications: ChatNotificationState = useChatNotifications(open, (target) => openChatRef.current(target))

  useEffect(() => {
    requestNotificationPermission()
  }, [])

  const value = useMemo(
    () => ({
      openChat,
      closeChat,
      isOpen: open,
      unreadCount: notifications.unreadCount,
      resetUnread: notifications.resetUnread,
      requestNotificationPermission: notifications.requestPermission,
      notificationPermission: notifications.permissionStatus,
    }),
    [open, openChat, closeChat, notifications.unreadCount, notifications.resetUnread, notifications.requestPermission, notifications.permissionStatus]
  )

  // TenantChatPanel children'ın kardeşidir; onay/bildirim için OpasDialogSaglayici ata olmalıdır
  // (kök layout'ta AppChatProviders'ın üstünde).
  return (
    <ChatPanelContext.Provider value={value}>
      {children}
      <TenantChatPanel open={open} onClose={closeChat} openTarget={openTarget} onOpenTargetConsumed={() => setOpenTarget(null)} />
    </ChatPanelContext.Provider>
  )
}

/** Navbar vb. layout dışı kullanımda null döner (yerel panel fallback) */
export function useChatPanelOptional(): ChatPanelContextValue | null {
  return useContext(ChatPanelContext)
}
